import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import DashboardPageLayout from '../components/DashboardPageLayout';
import { FileText, Save, Eye, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const OrgSettingsPage = () => {
  const [waiverText, setWaiverText] = useState('');
  const [emergencyPhone, setEmergencyPhone] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      // Load from localStorage for now (can upgrade to API later)
      const savedWaiver = localStorage.getItem('orgWaiverText');
      const savedEmergency = localStorage.getItem('orgEmergencyPhone');
      
      setWaiverText(savedWaiver || getDefaultWaiver());
      setEmergencyPhone(savedEmergency || '');
      setLoading(false);
    } catch (error) {
      console.error('Error loading settings:', error);
      setLoading(false);
    }
  };

  const getDefaultWaiver = () => {
    const orgName = localStorage.getItem('organizationName') || 'Charter Company';
    return `WAIVER AND RELEASE OF LIABILITY

By participating in this charter asset activity with ${orgName}, I acknowledge that I am engaging in an activity that involves inherent risks, including but not limited to:
- Risk of injury or death
- Risk of property damage or loss
- Risks associated with water activities, weather conditions, and asset operation

I hereby:
1. RELEASE, WAIVE, DISCHARGE, and COVENANT NOT TO SUE ${orgName}, its owners, operators, employees, and agents from any and all liability for any loss, damage, injury, or death that may be sustained by me or my property during this charter activity.

2. ASSUME ALL RISKS associated with participating in this charter activity, whether caused by the ordinary negligence of ${orgName} or otherwise.

3. AGREE that this waiver and release is legally binding on me, my heirs, executors, administrators, and assigns.

I have read and understood this waiver and release, and I voluntarily agree to its terms.`;
  };

  const handleSave = async () => {
    if (!waiverText.trim()) {
      toast.error('Waiver text cannot be empty');
      return;
    }

    setSaving(true);
    try {
      // Save to localStorage (can upgrade to API/database later)
      localStorage.setItem('orgWaiverText', waiverText);
      localStorage.setItem('orgEmergencyPhone', emergencyPhone);
      
      toast.success('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardPageLayout title="Organization Settings">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading settings...</p>
          </div>
        </div>
      </DashboardPageLayout>
    );
  }

  return (
    <DashboardPageLayout 
      title="Organization Settings"
      subtitle="Manage your waiver and emergency contact"
    >
      <div className="max-w-4xl space-y-6">
        {/* Emergency Contact */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Emergency Contact Number
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <Label>Emergency Phone Number</Label>
                <Input
                  type="tel"
                  value={emergencyPhone}
                  onChange={(e) => setEmergencyPhone(e.target.value.replace(/[^0-9+() -]/g, ''))}
                  placeholder="(512) 555-5555"
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  This number will appear on all customer trip tickets
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Custom Waiver */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Custom Waiver Text
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-900 font-medium mb-1">⚖️ Legal Protection</p>
                <p className="text-xs text-blue-700">
                  This waiver protects YOUR company. BookingFlows is NOT liable for the content or enforceability of your waiver. 
                  Consult with a lawyer to ensure it meets your needs.
                </p>
              </div>

              <div>
                <Label>Your Waiver Text</Label>
                <Textarea
                  value={waiverText}
                  onChange={(e) => setWaiverText(e.target.value)}
                  placeholder="Enter your custom waiver text..."
                  className="mt-1 min-h-[400px] font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {waiverText.length} characters • This is what customers will sign
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowPreview(true)}
                  className="flex items-center gap-2"
                >
                  <Eye className="w-4 h-4" />
                  Preview
                </Button>
                
                <Button
                  variant="outline"
                  onClick={() => setWaiverText(getDefaultWaiver())}
                >
                  Reset to Default
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 px-8"
          >
            {saving ? 'Saving...' : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Settings
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Preview Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Waiver Preview</DialogTitle>
          </DialogHeader>
          <div className="mt-4 bg-gray-50 rounded-lg border p-4 text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
            {waiverText}
          </div>
          <div className="mt-4">
            <Button onClick={() => setShowPreview(false)} className="w-full">
              Close Preview
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardPageLayout>
  );
};

export default OrgSettingsPage;
